import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

const values = [
  { title: "Compatibility First", description: "We prioritize matching based on lifestyle, habits, and values over just location and price.", icon: "🤝" },
  { title: "Community Focused", description: "Building relationships that extend beyond just sharing a living space.", icon: "🏘️" },
  { title: "Trust & Safety", description: "Verified profiles and secure communication channels for peace of mind.", icon: "🔒" },
];

const CARD_HEIGHT = 320; // fixed height so all three cards match

const cardSx = {
  borderRadius: "4px",
  backgroundColor: "#001d3d",
  border: "1px solid rgba(255,255,255,0.12)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  p: { xs: 2, md: 4 },
  height: "100%",
  minHeight: CARD_HEIGHT,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.4)",
  },
};

const ValueCard = ({ value, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    style={{ height: "100%", display: "flex", minWidth: 0 }}
  >
    <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", height: { xs: "auto", md: CARD_HEIGHT }, minHeight: { xs: 280, md: CARD_HEIGHT }, width: "100%" }}>
      <Typography variant="h3" sx={{ mb: 3, fontSize: { xs: "2rem", md: "3rem" }, lineHeight: 1, flexShrink: 0 }}>
        {value.icon}
      </Typography>
      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 700, fontSize: { xs: "1.25rem", md: "1.5rem" }, color: "#fff", flexShrink: 0 }}>
        {value.title}
      </Typography>
      <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem", flex: 1 }}>
        {value.description}
      </Typography>
    </Box>
  </motion.div>
);

const ValuesSection = () => (
  <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 4 }, backgroundColor: "#fff" }}>
    <Box sx={{ maxWidth: "1200px", mx: "auto", textAlign: "center", mb: 8 }}>
      <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "2rem", md: "3rem" },
            background: "linear-gradient(45deg, #001d3d 0%, #42a5f5 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Our Core Values
        </Typography>
        <Typography variant="h6" component="p" sx={{ maxWidth: "700px", mx: "auto", color: "#616161", fontSize: { xs: "1rem", md: "1.25rem" } }}>
          These principles guide everything we do at FindMyRoommates
        </Typography>
      </motion.div>
    </Box>
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "stretch",
        gap: 3,
        rowGap: 3,
        columnGap: 3,
        width: "100%",
        maxWidth: "1200px",
        mx: "auto",
        px: { xs: 0, sm: 2 },
      }}
    >
      {values.map((value, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            flex: "1 1 100%",
            minWidth: 0,
            maxWidth: { sm: "calc(50% - 12px)", md: 380 },
            marginBottom: 3,
            "&:last-child": { marginBottom: 0 },
            "@media (min-width:600px) and (max-width:899px)": { flex: "1 1 calc(50% - 12px)" },
            "@media (min-width:900px)": { flex: "0 0 380px" },
          }}
        >
          <ValueCard value={value} index={index} />
        </Box>
      ))}
    </Box>
  </Box>
);

export default ValuesSection;